/* A single owner for ranked search requests, progressive results, and Back restoration. */
(() => {
  'use strict';

  if (window.FAFSearchPage) return;

  const wrapperSelector = '[data-faf-search-page][data-section-id]';
  const facetFormSelector = 'facet-filters-form form';
  const asParams = (value) => new URLSearchParams(value instanceof URLSearchParams ? value.toString() : value || '');
  const pageSize = 24;
  const restoreLimit = 50;
  const storageKey = 'faf-search-scroll-v1';
  const storageLifetime = 60 * 60 * 1000;

  class SearchPageController {
    constructor() {
      this.requestId = 0;
      this.controller = null;
      this.lastParams = asParams(window.location.search);
      this.initialized = false;
      this.pendingTimer = null;
      this.initialDocumentTitle = document.title;
      this.initialBreadcrumbText = null;
      this.moreController = null;
      this.moreLoading = false;
      this.progress = null;
      this.observer = null;
      this.restoring = false;
      this.scrollTimer = null;
      document.addEventListener('submit', this.onSubmit.bind(this), true);
      document.addEventListener('input', this.onInput.bind(this), true);
      document.addEventListener('click', this.onClick.bind(this), true);
      window.addEventListener('popstate', () => {
        if (this.isActive()) this.renderPage(window.location.search, null, false, this.savedView());
      });
      document.addEventListener('shopify:section:load', () => this.start());
      window.addEventListener('pagehide', () => { this.savePosition(); this.cancel(); });
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted || !this.wrapper) return;
        if (this.wrapper.dataset.fafSearchState !== 'ready') this.renderPage(window.location.search, null, false, this.savedView());
        else if (this.progress) {
          this.progress.enabled = true;
          this.moreLoading = false;
          this.restoring = false;
          this.setMoreStatus('ready');
          this.observeMore();
        }
      });
      window.addEventListener('scroll', () => {
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => this.savePosition(), 200);
      }, { passive: true });
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => this.start(), { once: true });
      else this.start();
    }

    get wrapper() { return document.querySelector(wrapperSelector); }
    isActive() { return Boolean(this.wrapper); }

    start() {
      const wrapper = this.wrapper;
      if (!wrapper || wrapper.dataset.fafSearchMounted === 'true') return;
      if (this.initialBreadcrumbText === null) this.initialBreadcrumbText = this.breadcrumb()?.textContent.trim() || '';
      wrapper.dataset.fafSearchMounted = 'true';
      if (!(asParams(window.location.search).get('q') || '').trim()) {
        wrapper.dataset.fafSearchReady = 'true';
        wrapper.dataset.fafSearchState = 'idle';
        return;
      }
      this.renderPage(window.location.search, null, false, this.savedView());
    }

    cancel() {
      this.requestId += 1;
      this.controller?.abort();
      this.controller = null;
      this.moreController?.abort();
      this.moreController = null;
      this.moreLoading = false;
      this.observer?.disconnect();
      this.observer = null;
      if (this.progress) this.progress.enabled = false;
      clearTimeout(this.pendingTimer);
    }

    publicParams(value) {
      const params = asParams(value);
      params.delete('section_id');
      params.delete('sections');
      params.delete('sections[]');
      if (!params.has('q')) params.set('q', asParams(window.location.search).get('q') || '');
      const plan = window.FAFSearch?.plan?.(params.get('q'));
      if (plan?.colors?.length) params.delete('filter.v.option.color');
      if (plan?.sizes?.length) params.delete('filter.v.option.size');
      if (plan?.types?.length) params.delete('filter.p.product_type');
      return params;
    }

    pageNumber(params) {
      const value = params.get('page') || '1';
      return /^\d+$/.test(value) && Number.isSafeInteger(Number(value)) && Number(value) > 0 ? Number(value) : 1;
    }

    viewSignature(params = this.lastParams) {
      const ordered = asParams(params);
      ordered.sort();
      return [window.Shopify?.theme?.id || '', window.Shopify?.country || '', window.Shopify?.currency?.active || '', document.documentElement.lang, location.pathname, ordered.toString()].join('|');
    }

    storedViews() {
      try {
        const values = JSON.parse(sessionStorage.getItem(storageKey) || '[]');
        return Array.isArray(values) ? values.filter(item => item && Date.now() - item.savedAt < storageLifetime).slice(-8) : [];
      } catch { return []; }
    }

    savedView() {
      const historyView = history.state?.fafSearchScroll;
      if (!historyView || typeof historyView.key !== 'string') return null;
      const saved = this.storedViews().find(item => item.key === historyView.key) || historyView;
      const signature = this.viewSignature(this.publicParams(window.location.search));
      if (saved.signature !== signature || !Number.isInteger(saved.startPage) || !Number.isInteger(saved.loadedPage) ||
        saved.startPage !== this.pageNumber(asParams(window.location.search)) || saved.loadedPage < saved.startPage ||
        saved.loadedPage - saved.startPage >= restoreLimit || !Number.isFinite(saved.scrollY) || saved.scrollY < 0 ||
        Date.now() - saved.savedAt >= storageLifetime) return null;
      return saved;
    }

    productId(card) {
      try {
        const data = JSON.parse(card.querySelector('[data-faf-search-product-data]')?.textContent || 'null');
        return data?.id ? String(data.id) : '';
      } catch { return ''; }
    }

    savePosition() {
      const progress = this.progress;
      if (!progress?.enabled || this.restoring || this.wrapper?.dataset.fafSearchState !== 'ready') return;
      if (this.viewSignature(this.publicParams(location.search)) !== progress.signature) return;
      const cards = Array.from(progress.list.children);
      const anchor = cards.find(card => card.getBoundingClientRect().bottom > 0);
      const saved = {
        key: progress.key, signature: progress.signature,
        startPage: progress.startPage, loadedPage: progress.page,
        scrollY: Math.max(0, window.scrollY), savedAt: Date.now(),
        anchor: anchor ? { id: this.productId(anchor), offset: anchor.getBoundingClientRect().top } : null,
      };
      try {
        history.replaceState({ ...history.state, fafSearchScroll: saved }, '', location.href);
        const values = this.storedViews().filter(item => item.key !== saved.key);
        values.push(saved);
        sessionStorage.setItem(storageKey, JSON.stringify(values.slice(-8)));
      } catch { /* Private browsing and full storage must not interrupt shopping. */ }
    }

    prepareCards(root, page) {
      root.querySelectorAll('#product-grid li[data-faf-search-product]').forEach((card, index) => {
        const suffix = '-faf-' + this.requestId + '-' + page + '-' + index;
        const ids = new Map();
        [card, ...card.querySelectorAll('[id]')].forEach(element => {
          if (!element.id) return;
          const old = element.id;
          element.id += suffix;
          ids.set(old, element.id);
        });
        card.querySelectorAll('*').forEach(element => {
          ['for', 'form', 'aria-labelledby', 'aria-describedby', 'aria-controls', 'aria-owns'].forEach(name => {
            const value = element.getAttribute(name);
            if (value) element.setAttribute(name, value.split(/\s+/).map(id => ids.get(id) || id).join(' '));
          });
          ['href', 'data-modal'].forEach(name => {
            const value = element.getAttribute(name);
            if (value?.startsWith('#') && ids.has(value.slice(1))) element.setAttribute(name, '#' + ids.get(value.slice(1)));
          });
        });
        card.querySelectorAll('quick-add-modal, quick-view-modal').forEach(modal => { modal.dataset.fafSearchOwned = 'true'; });
      });
    }

    setMoreStatus(state) {
      const progress = this.progress;
      if (!progress?.controls) return;
      if (state === 'ready' && !this.hasMore(progress)) state = 'complete';
      const strings = window.FAFSearch?.strings || {};
      const controls = progress.controls;
      controls.dataset.fafSearchMoreState = state;
      const button = controls.querySelector('[data-faf-search-more-button]');
      const status = controls.querySelector('[data-faf-search-more-status]');
      button.disabled = state === 'loading' || state === 'restoring';
      button.hidden = state === 'complete';
      controls.querySelector('[data-faf-search-more-label]').textContent = strings.loadMore || 'Load more results';
      const progressFormat = progress.stream ? (strings.resultsLoaded || '{shown} results loaded') : (strings.resultsProgress || 'Showing {shown} of {total} results');
      controls.querySelector('[data-faf-search-more-progress]').textContent = progressFormat
        .replace('{shown}', String(progress.ids.size)).replace('{total}', String(progress.total));
      status.textContent = state === 'error' ? (strings.loadMoreError || 'More results could not load. Select Load more to try again.')
        : state === 'loading' ? (strings.loadingMore || 'Loading more results…')
          : state === 'restoring' ? (strings.restoringResults || 'Restoring your results…')
            : state === 'complete' ? (strings.allLoaded || 'You’ve reached the end of the results.') : '';
      status.hidden = !status.textContent;
      progress.list.setAttribute('aria-busy', String(state === 'loading' || state === 'restoring'));
    }

    nativePagination(visible) {
      this.wrapper?.querySelectorAll('.pagination-wrapper').forEach(element => { element.hidden = !visible; });
    }

    hasMore(progress = this.progress) {
      return Boolean(progress && (progress.stream ? !progress.streamDone : progress.page < progress.lastPage));
    }

    addStartLink(container, params) {
      const first = document.createElement('a');
      const firstParams = asParams(params);
      firstParams.delete('page');
      first.href = location.pathname + '?' + firstParams.toString();
      first.dataset.fafSearchPageLink = '';
      first.className = 'faf-search-more__start';
      first.textContent = window.FAFSearch?.strings?.startResults || 'Start from the first result';
      container.append(first);
      return first;
    }

    setupMore(wrapper, params, plan, saved, streamContext = null) {
      this.progress = null;
      const cards = Array.from(wrapper.querySelectorAll('#product-grid li[data-faf-search-product]'));
      const list = cards[0]?.parentElement;
      const total = Number(wrapper.querySelector('[data-faf-search-count]')?.dataset.fafSearchCount);
      const page = this.pageNumber(params);
      const expected = Math.min(pageSize, Math.max(0, total - (page - 1) * pageSize));
      const ids = cards.map(card => this.productId(card));
      // Deep direct links retain immediate native pagination. Rebuilding a
      // ranked cursor from dozens of earlier pages would delay these shoppers.
      if (page > 3 && window.FAFSearchRanking?.createStream) {
        const firstLink = document.createElement('div');
        firstLink.className = 'faf-search-more';
        this.addStartLink(firstLink, params);
        wrapper.querySelector('#product-grid')?.append(firstLink);
        return;
      }
      // Service and mixed result groups keep the theme's complete paginated view.
      if (!window.FAFSearchRanking?.fetchSection || !plan.productIntent || !list || !Number.isSafeInteger(total) || total < 1 ||
        (streamContext ? cards.length > pageSize || (!streamContext.result.done && cards.length !== pageSize) : cards.length !== expected) ||
        cards.some(card => card.parentElement !== list) || ids.some(id => !id) || new Set(ids).size !== ids.length ||
        wrapper.querySelector('#product-grid .template-search__item:not([data-faf-search-product])')) return;
      const controls = document.createElement('div');
      controls.className = 'faf-search-more';
      controls.dataset.fafSearchMore = '';
      const progressText = document.createElement('p');
      progressText.className = 'faf-search-more__progress';
      progressText.dataset.fafSearchMoreProgress = '';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'button button--secondary';
      button.dataset.fafSearchMoreButton = '';
      const spinner = document.createElement('span');
      spinner.className = 'faf-search-more__spinner';
      spinner.setAttribute('aria-hidden', 'true');
      const label = document.createElement('span');
      label.dataset.fafSearchMoreLabel = '';
      button.append(spinner, label);
      const status = document.createElement('p');
      status.className = 'faf-search-more__status';
      status.dataset.fafSearchMoreStatus = '';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('aria-atomic', 'true');
      const sentinel = document.createElement('div');
      sentinel.className = 'faf-search-more__sentinel';
      sentinel.dataset.fafSearchMoreSentinel = '';
      sentinel.setAttribute('aria-hidden', 'true');
      controls.append(progressText, button, status, sentinel);
      if (page > 1) this.addStartLink(controls, params);
      list.insertAdjacentElement('afterend', controls);
      this.progress = {
        list, controls, ids: new Set(ids), total, page, startPage: page, lastPage: Math.ceil(total / pageSize),
        params: asParams(params), plan, enabled: true, autoBlocked: false,
        signature: this.viewSignature(params), key: saved?.key || (Date.now().toString(36) + '-' + Math.random().toString(36).slice(2)),
        stream: streamContext?.stream || null, streamDone: streamContext?.result.done || false,
        streamLoaded: streamContext?.result.loaded || 0, pendingBatch: null,
      };
      if (streamContext) wrapper.dataset.fafSearchStream = 'true';
      wrapper.dataset.fafSearchInfinite = 'true';
      wrapper.dataset.fafLoadedPage = String(page);
      wrapper.dataset.fafStartPage = String(page);
      this.nativePagination(false);
      this.setMoreStatus('ready');
      if (!saved) { this.savePosition(); this.observeMore(); }
    }

    observeMore() {
      this.observer?.disconnect();
      this.observer = null;
      const progress = this.progress;
      if (!progress?.enabled || progress.autoBlocked || this.restoring || !this.hasMore(progress) || typeof window.IntersectionObserver !== 'function') return;
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting) && this.progress === progress) this.loadMore(false);
      }, { rootMargin: '0px 0px 500px 0px' });
      this.observer.observe(progress.controls.querySelector('[data-faf-search-more-sentinel]'));
    }

    async loadMore(manual = false) {
      const progress = this.progress;
      const wrapper = this.wrapper;
      if (!progress?.enabled || this.moreLoading || !this.hasMore(progress) || (!manual && progress.autoBlocked)) return false;
      const requestId = this.requestId;
      const page = progress.page + 1;
      const params = asParams(progress.params);
      params.set('page', String(page));
      this.moreLoading = true;
      this.observer?.disconnect();
      const controller = new AbortController();
      this.moreController = controller;
      const signal = controller.signal;
      const current = () => !signal.aborted && requestId === this.requestId && this.progress === progress && this.wrapper === wrapper;
      this.setMoreStatus(this.restoring ? 'restoring' : 'loading');
      try {
        let batch = null;
        if (progress.stream) {
          batch = progress.pendingBatch || await progress.stream.next({ signal });
          if (current()) progress.pendingBatch = batch;
        }
        const parsed = batch ? batch.section : await window.FAFSearchRanking.fetchSection(params, wrapper.dataset.sectionId, { signal, pageSize });
        if (!current()) return false;
        const next = parsed.querySelector(wrapperSelector);
        let nextCards = Array.from(next?.querySelectorAll('#product-grid li[data-faf-search-product]') || []);
        const total = Number(next?.querySelector('[data-faf-search-count]')?.dataset.fafSearchCount);
        const expected = Math.min(pageSize, Math.max(0, progress.total - (page - 1) * pageSize));
        // Shopify can return a full final page even when it advertises a
        // 1,000-result window. Append only the remaining advertised positions.
        if (!batch && total === 1000 && page === progress.lastPage && expected > 0 && nextCards.length > expected && nextCards.length <= pageSize) {
          const rawIds = nextCards.map(card => this.productId(card));
          if (rawIds.every(Boolean) && new Set(rawIds).size === rawIds.length) nextCards = nextCards.slice(0, expected);
        }
        const ids = nextCards.map(card => this.productId(card));
        const invalidSize = batch
          ? typeof batch.done !== 'boolean' || !Number.isSafeInteger(batch.loaded) || batch.loaded !== progress.streamLoaded + nextCards.length ||
            nextCards.length > pageSize || (!batch.done && nextCards.length !== pageSize)
          : total !== progress.total || nextCards.length !== expected || !expected;
        if (!next || invalidSize ||
          ids.some(id => !id || progress.ids.has(id)) || new Set(ids).size !== ids.length ||
          next.querySelector('#product-grid .template-search__item:not([data-faf-search-product])') ||
          next.dataset.fafBrandPriority !== wrapper.dataset.fafBrandPriority ||
          (!batch && (next.dataset.fafPreferredCount !== wrapper.dataset.fafPreferredCount || next.dataset.fafOtherCount !== wrapper.dataset.fafOtherCount))) {
          throw new Error('Search results changed; use the paginated view.');
        }
        await window.FAFSearch.normalize(next, params.get('q') || '', progress.plan);
        this.prepareCards(next, page);
        // Enhance the incoming cards only: existing shoppers' swatch choices stay put.
        await window.FAFSearch.enhanceCards?.(next, progress.cardPlan);
        if (!current()) return false;
        const fragment = document.createDocumentFragment();
        nextCards.forEach(card => fragment.append(card));
        progress.list.append(fragment);
        ids.forEach(id => progress.ids.add(id));
        if (nextCards.length) progress.page = page;
        if (batch) {
          progress.streamDone = batch.done;
          progress.streamLoaded = batch.loaded;
          progress.pendingBatch = null;
          progress.total = batch.done ? batch.loaded : Math.max(batch.loaded, Number(batch.totalEstimate) || progress.total);
          if (batch.done) {
            wrapper.querySelector('[data-faf-search-count]').dataset.fafSearchCount = String(batch.loaded);
            const strings = window.FAFSearch?.strings || {};
            const format = batch.loaded === 1 ? strings.oneResult : strings.results;
            if (format) wrapper.querySelectorAll('#ProductCount,#ProductCountDesktop,.facets__product-count,[data-faf-search-live-count]').forEach(element => {
              element.textContent = format.replace('{count}', String(batch.loaded)).replace('{query}', params.get('q') || '');
            });
            this.updatePageTitle(wrapper, params.get('q') || '');
          }
        }
        progress.autoBlocked = false;
        wrapper.dataset.fafLoadedPage = String(progress.page);
        const oldPagination = wrapper.querySelector('.pagination-wrapper');
        const newPagination = next.querySelector('.pagination-wrapper');
        if (newPagination) {
          newPagination.hidden = true;
          if (oldPagination) oldPagination.replaceWith(newPagination);
          else progress.controls.insertAdjacentElement('afterend', newPagination);
        } else oldPagination?.remove();
        if (typeof colorSwatches === 'function') colorSwatches();
        this.setMoreStatus(this.restoring ? 'restoring' : 'ready');
        if (manual && !this.restoring && nextCards.length) {
          const first = nextCards[0].querySelector('a[href]');
          first?.focus({ preventScroll: true });
        }
        this.savePosition();
        document.dispatchEvent(new CustomEvent('faf:search-page-appended', { detail: { page: progress.page, manual, root: wrapper, searchParams: params.toString() } }));
        return true;
      } catch (error) {
        if (!current()) return false;
        progress.autoBlocked = true;
        this.setMoreStatus('error');
        this.nativePagination(true);
        if (!progress.controls.querySelector('.faf-search-more__start')) this.addStartLink(progress.controls, progress.params);
        return false;
      } finally {
        if (current()) {
          this.moreLoading = false;
          this.moreController = null;
          this.observeMore();
        }
      }
    }

    async restorePosition(saved, requestId) {
      const progress = this.progress;
      if (!saved || !progress) return;
      this.restoring = true;
      this.setMoreStatus('restoring');
      const target = Math.min(saved.loadedPage, progress.stream ? Infinity : progress.lastPage, progress.startPage + restoreLimit - 1);
      while (progress.page < target && this.progress === progress && requestId === this.requestId) {
        if (!await this.loadMore(true)) break;
      }
      if (this.progress !== progress || requestId !== this.requestId) return;
      const cards = Array.from(progress.list.children);
      const anchor = saved.anchor?.id ? cards.find(card => this.productId(card) === saved.anchor.id) : null;
      const position = () => {
        if (this.progress !== progress || requestId !== this.requestId) return;
        window.scrollTo({ top: Math.max(0, anchor && Number.isFinite(saved.anchor.offset)
          ? window.scrollY + anchor.getBoundingClientRect().top - saved.anchor.offset : saved.scrollY), behavior: 'instant' });
      };
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (this.progress !== progress || requestId !== this.requestId) return;
      position();
      this.restoring = false;
      this.setMoreStatus(progress.autoBlocked ? 'error' : 'ready');
      this.savePosition();
      this.observeMore();
      document.dispatchEvent(new CustomEvent('faf:search-position-restored', { detail: { page: progress.page, root: this.wrapper } }));
    }

    breadcrumb() {
      return document.querySelector('nav.breadcrumb > span:last-child');
    }

    updatePageTitle(wrapper, query) {
      const count = Number(wrapper.querySelector('[data-faf-search-count]')?.dataset.fafSearchCount);
      const strings = window.FAFSearch?.strings || {};
      const format = count === 1 ? strings.oneResult : strings.results;
      const label = Number.isFinite(count) && format
        ? format.replace('{count}', String(count)).replace('{query}', query)
        : wrapper.querySelector('.main-page-title')?.textContent.trim();
      if (!label) return;
      const breadcrumb = this.breadcrumb();
      if (breadcrumb) breadcrumb.textContent = label;
      if (this.initialBreadcrumbText && this.initialDocumentTitle.includes(this.initialBreadcrumbText)) {
        document.title = this.initialDocumentTitle.replace(this.initialBreadcrumbText, label);
      }
    }

    requestStatus(wrapper) {
      let panel = wrapper.querySelector('[data-faf-search-feedback]');
      if (panel) return panel;
      panel = document.createElement('div');
      panel.className = 'faf-search-feedback container';
      panel.dataset.fafSearchFeedback = '';
      const message = document.createElement('p');
      message.dataset.fafSearchMessage = '';
      message.setAttribute('role', 'status');
      message.setAttribute('aria-live', 'polite');
      message.setAttribute('aria-atomic', 'true');
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'button button--secondary';
      retry.dataset.fafSearchRetry = '';
      retry.textContent = window.FAFSearch?.strings?.retry || wrapper.dataset.fafSearchRetryText || 'Try again';
      retry.hidden = true;
      panel.append(message, retry);
      const header = wrapper.querySelector('.template-search__header');
      if (header) header.insertAdjacentElement('afterend', panel);
      else wrapper.prepend(panel);
      return panel;
    }

    setStatus(state, wrapper = this.wrapper) {
      if (!wrapper) return;
      wrapper.dataset.fafSearchState = state;
      const results = wrapper.querySelector('[data-faf-search-results]') || wrapper.querySelector('#ProductGridContainer');
      results?.setAttribute('aria-busy', String(state === 'loading'));
      const panel = this.requestStatus(wrapper);
      const message = panel.querySelector('[data-faf-search-message]');
      const strings = window.FAFSearch?.strings || {};
      panel.querySelector('[data-faf-search-retry]').hidden = state !== 'error';
      const firstLink = panel.querySelector('.faf-search-more__start') || (state === 'error' ? this.addStartLink(panel, this.lastParams) : null);
      if (firstLink) firstLink.hidden = state !== 'error';
      panel.hidden = state === 'ready' || state === 'idle';
      message.textContent = state === 'error'
        ? (strings.error || wrapper.dataset.fafSearchErrorText || 'We could not load these search results. Please try again.')
        : state === 'loading'
          ? (strings.loading || wrapper.dataset.fafSearchLoadingText || 'Finding your matches…')
          : '';
    }

    captureView(wrapper) {
      const active = document.activeElement;
      const focus = wrapper.contains(active) ? {
        id: active.id,
        name: active.getAttribute('name'),
        tag: active.tagName,
        type: active.getAttribute('type'),
        value: active.value,
        start: active.selectionStart,
        end: active.selectionEnd,
        formId: active.form?.id,
      } : null;
      const details = Array.from(wrapper.querySelectorAll('details')).map((detail, index) => ({
        index,
        id: detail.id,
        drawer: detail.parentElement?.matches('menu-drawer.mobile-facets__wrapper'),
        open: detail.open,
        opening: detail.classList.contains('menu-opening'),
      }));
      return { focus, details };
    }

    restoreView(wrapper, view) {
      const details = Array.from(wrapper.querySelectorAll('details'));
      view.details.forEach((saved) => {
        const detail = saved.id ? details.find((item) => item.id === saved.id)
          : saved.drawer ? wrapper.querySelector('menu-drawer.mobile-facets__wrapper > details') : details[saved.index];
        if (!detail) return;
        detail.open = saved.open;
        detail.classList.toggle('menu-opening', saved.open && saved.opening);
        detail.querySelector(':scope > summary')?.setAttribute('aria-expanded', String(saved.open));
      });
      if (!view.focus) return;
      const saved = view.focus;
      let target = saved.id ? Array.from(wrapper.querySelectorAll('[id]')).find((item) => item.id === saved.id) : null;
      if (!target && saved.name) {
        target = Array.from(wrapper.querySelectorAll('[name]')).find((item) =>
          item.name === saved.name && item.tagName === saved.tag &&
          (!saved.formId || item.form?.id === saved.formId) &&
          (!['checkbox', 'radio'].includes(saved.type) || item.value === saved.value));
      }
      if (!target || target.disabled) return;
      if (target.matches('input:not([type="checkbox"]):not([type="radio"]):not([type="range"]), textarea')) target.value = saved.value;
      target.focus({ preventScroll: true });
      if (typeof target.setSelectionRange === 'function' && saved.start !== null && saved.start !== undefined) {
        try { target.setSelectionRange(saved.start, saved.end); } catch { /* Number inputs do not expose text selection. */ }
      }
      const openDrawer = target.closest('menu-drawer');
      const drawerDetails = openDrawer?.querySelector(':scope > details[open]');
      const drawerContent = drawerDetails?.querySelector('.mobile-facets');
      if (drawerContent && typeof trapFocus === 'function') trapFocus(drawerContent, target);
    }

    async renderPage(searchParams, event = null, updateURLHash = true, saved = null) {
      const wrapper = this.wrapper;
      if (!wrapper) return;
      if (event?.target instanceof Element && !event.target.isConnected) return;
      const sourceForm = event?.target instanceof Element && ['input', 'submit'].includes(event.type)
        ? event.target.closest(facetFormSelector) : null;
      const params = sourceForm && wrapper.contains(sourceForm) ? this.formParams(sourceForm) : this.publicParams(searchParams);
      if (updateURLHash) this.savePosition();
      this.lastParams = asParams(params);
      this.cancel();
      this.restoring = false;
      const requestId = this.requestId;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const query = params.get('q') || '';
      const shouldScroll = event?.fafSearchNavigation === true;
      const publicURL = new URL(window.location.href);
      publicURL.search = params.toString();
      if (updateURLHash && publicURL.href !== window.location.href) {
        history.pushState({ ...history.state, searchParams: params.toString(), fafSearchPage: true, fafSearchScroll: null }, '', publicURL);
      } else if (!updateURLHash) {
        const currentParams = asParams(window.location.search);
        const cleanedParams = this.publicParams(currentParams);
        if (cleanedParams.toString() !== currentParams.toString() && cleanedParams.toString() === params.toString()) {
          history.replaceState({ ...history.state, searchParams: params.toString(), fafSearchPage: true }, '', publicURL);
        }
      }
      this.setStatus('loading', wrapper);
      try {
        if (!window.FAFSearch?.plan || !window.FAFSearch?.toEngineParams || !window.FAFSearch?.normalize) throw new Error('Search integration unavailable');
        const plan = window.FAFSearch.plan(query);
        const cardPlan = {
          ...plan,
          colors: plan.colors?.length ? plan.colors : params.getAll('filter.v.option.color').filter(Boolean),
          sizes: plan.sizes?.length ? plan.sizes : params.getAll('filter.v.option.size').filter(Boolean),
        };
        let parsed;
        let streamContext = null;
        const requestedPage = this.pageNumber(params);
        if (window.FAFSearchRanking?.createStream && plan.productIntent && requestedPage <= 3) {
          const stream = await window.FAFSearchRanking.createStream(params, wrapper.dataset.sectionId, { signal, pageSize });
          if (stream) {
            let result = stream;
            let reachedPage = 1;
            while (reachedPage < requestedPage && !result.done) {
              result = await stream.next({ signal });
              reachedPage += 1;
            }
            if (reachedPage === requestedPage) {
              streamContext = { stream, result };
              parsed = result.section;
            }
          }
        }
        if (!parsed && window.FAFSearchRanking?.fetchSection) {
          parsed = await window.FAFSearchRanking.fetchSection(params, wrapper.dataset.sectionId, { signal, pageSize: 24 });
        } else if (!parsed) {
          const engineParams = asParams(window.FAFSearch.toEngineParams(asParams(params)));
          engineParams.set('section_id', wrapper.dataset.sectionId);
          const requestURL = new URL(window.location.pathname, window.location.origin);
          requestURL.search = engineParams.toString();
          const response = await fetch(requestURL.href, { signal, credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
          if (!response.ok) throw new Error('Search request failed');
          parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        }
        if (requestId !== this.requestId || signal.aborted) return;
        const next = parsed.querySelector(wrapperSelector);
        if (!next || !next.querySelector('form[role="search"]')) throw new Error('Invalid search response');
        if (streamContext?.result.done) next.querySelector('[data-faf-search-count]').dataset.fafSearchCount = String(streamContext.result.loaded);
        await window.FAFSearch.normalize(next, query, plan);
        if (requestId !== this.requestId || signal.aborted || this.wrapper !== wrapper) return;
        const view = this.captureView(wrapper);
        // The theme moves quick-view modals to body when connected. Clean up
        // only modals owned by the previous search cards, including initial HTML.
        wrapper.querySelectorAll('modal-opener[data-modal]').forEach(opener => {
          const id = opener.getAttribute('data-modal');
          const modal = id?.startsWith('#') ? document.getElementById(id.slice(1)) : null;
          if (modal?.matches('quick-add-modal, quick-view-modal')) modal.remove();
        });
        document.querySelectorAll('body > quick-add-modal[data-faf-search-owned], body > quick-view-modal[data-faf-search-owned]').forEach(modal => modal.remove());
        this.prepareCards(next, this.pageNumber(params));
        next.dataset.fafSearchMounted = 'true';
        next.dataset.fafSearchState = 'loading';
        wrapper.replaceWith(next);
        await window.FAFSearch.enhanceCards?.(next, cardPlan);
        if (requestId !== this.requestId || signal.aborted || this.wrapper !== next) return;
        if (typeof colorSwatches === 'function') colorSwatches();
        next.dataset.fafSearchReady = 'true';
        this.initialized = true;
        this.setStatus('ready', next);
        this.updatePageTitle(next, query);
        this.restoreView(next, view);
        this.setupMore(next, params, plan, saved, streamContext);
        if (this.progress) this.progress.cardPlan = cardPlan;
        if (shouldScroll) next.querySelector('.main-page-title')?.scrollIntoView({ block: 'start', behavior: 'auto' });
        document.dispatchEvent(new CustomEvent('faf:search-page-rendered', { detail: { query, plan, searchParams: params.toString(), root: next } }));
        if (saved) await this.restorePosition(saved, requestId);
      } catch (error) {
        if (signal.aborted || requestId !== this.requestId) return;
        this.setStatus('error');
      }
    }

    formParams(form) {
      const params = this.publicParams(window.location.search);
      params.delete('page');
      Array.from(params.keys()).forEach((key) => { if (key.startsWith('filter.')) params.delete(key); });
      const wrapper = this.wrapper;
      const mobile = form.id === 'FacetFiltersFormMobile' || Boolean(form.closest('.mobile-facets__wrapper'));
      const forms = mobile ? [form] : Array.from(wrapper.querySelectorAll(facetFormSelector)).filter((item) =>
        item.id !== 'FacetFiltersFormMobile' && !item.closest('.mobile-facets__wrapper'));
      if (!forms.length) forms.push(form);
      const values = new Map();
      forms.forEach((item) => new FormData(item).forEach((value, name) => {
        if (typeof value !== 'string') return;
        if (!values.has(name)) values.set(name, []);
        if (!values.get(name).includes(value)) values.get(name).push(value);
      }));
      values.forEach((items, name) => {
        params.delete(name);
        items.forEach((value) => params.append(name, value));
      });
      return params;
    }

    onSubmit(event) {
      const wrapper = this.wrapper;
      const form = event.target;
      if (!wrapper?.contains(form) || !(form instanceof HTMLFormElement)) return;
      if (form.matches('[role="search"]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const query = form.querySelector('[name="q"]')?.value || '';
        if (!query.trim()) { form.querySelector('[name="q"]')?.focus(); return; }
        // A fresh query starts fresh: old filters, explicit type and page never follow it.
        const params = new URLSearchParams({ q: query, 'options[prefix]': 'last' });
        this.renderPage(params, { fafSearchNavigation: true });
      } else if (form.closest('facet-filters-form')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.renderPage(this.formParams(form), event);
      }
    }

    onInput(event) {
      const target = event.target;
      const form = target.form;
      if (!form || !this.wrapper?.contains(form) || !form.closest('facet-filters-form')) return;
      // Cancel an old response before the native 500 ms debounce. Do not stop the
      // event: price-range target handlers must synchronize their number fields.
      this.savePosition();
      this.cancel();
      if (!customElements.get('facet-filters-form')) {
        this.pendingTimer = setTimeout(() => this.renderPage(this.formParams(form), event), 500);
      }
    }

    onClick(event) {
      if (!(event.target instanceof Element)) return;
      const wrapper = this.wrapper;
      if (!wrapper?.contains(event.target)) return;
      if (event.target.closest('[data-faf-search-more-button]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.loadMore(true);
        return;
      }
      if (event.target.closest('li[data-faf-search-product] a[href]')) this.savePosition();
      if (event.target.closest('[data-faf-search-retry]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.renderPage(this.lastParams, null, false, this.savedView());
        return;
      }
      const link = event.target.closest('facet-remove a[href], .pagination a[href], .pagination-wrapper a[href], [data-faf-search-page-link][href]');
      if (!link || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || link.target === '_blank' || link.hasAttribute('download')) return;
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.renderPage(url.search, { fafSearchNavigation: Boolean(link.closest('.pagination, .pagination-wrapper') || link.matches('[data-faf-search-page-link]')) });
    }
  }

  window.FAFSearchPage = new SearchPageController();
})();
